/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CopyLinkIcon, LogoWithText, PrecisionLogo, PrecisionLogoMain } from 'assets';
import {
  BasicExpectationsTable,
  CollapsibleSection,
  DoughnutChart,
  PersonalityAlignmentBadge,
  PersonalityAlignmentChart,
  SkillsComparisonChart,
} from "components";
import {
  useReportDetails,
  useAssessmentSections,
  isSectionEnabled,
} from 'hooks';
import { getScoreColor, getScoreBgClass, getScoreTextClass, getBadgeBgClass } from 'utils/scoreColor';
import { formatSalaryExpectation, formatSalaryRange } from 'utils/salary';
import { formatPersonName } from 'utils/titleCase';


const CollapsibleCard: React.FC<{ title: string; children: React.ReactNode; defaultOpen?: boolean }> = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg bg-[#FAFAFB] mb-4 last:mb-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-4 bg-transparent border-0 rounded-lg cursor-pointer text-[14px] text-[#101323] transition-colors hover:bg-[#F2F4F7]"
      >
        <span className="text-left">{title}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <path d="M4 6L8 10L12 6" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-4 pb-4">
          <p className="text-[14px] text-[#667085] leading-relaxed m-0">{children}</p>
        </div>
      )}
    </div>
  );
};

const DomainKnowledgeResults: React.FC<{
  domainKnowledge: any;
  ScoreBadge: React.FC<{ score: number; max?: number }>;
}> = ({ domainKnowledge, ScoreBadge }) => {
  return (
    <div className="mt-5">

      {domainKnowledge.results.map((result: any, index: number) => (
        <div key={index} className="mb-8 last:mb-0">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-base font-medium text-[#1D2939] m-0">
              {result.question_content || 'N/A'}
            </h3>
            <ScoreBadge score={result.points_earned ?? 0} max={result.points_possible ?? 10} />
          </div>

          <CollapsibleCard title="Score reason:" defaultOpen>
            {result.reason || result.score_reason || 'N/A'}
          </CollapsibleCard>

          <CollapsibleCard title="Candidates answer:" defaultOpen>
            {result.answer || 'N/A'}
          </CollapsibleCard>
        </div>
      ))}
    </div>
  );
};

const PublicReportUI = () => {
  const { id: reportId } = useParams<{ id: string }>();
  // Fetch report data using general report endpoint (unauthorised)
  const { reportDetails, isLoading: detailsLoading } = useReportDetails(reportId);

  // Extract data from general report endpoint
  const basicExpectation = reportDetails?.basic_expectation;
  const softSkills = reportDetails?.soft_skills;
  const workStyle = reportDetails?.work_style;
  const workplaceQualities = reportDetails?.work_place_qualities;
  const workMotivator = reportDetails?.work_motivator;
  const personality = reportDetails?.personality;
  const domainKnowledge = reportDetails?.domain_knowledge;

  // Hide sections for metrics the recruiter deleted in the hiring flow. Falls
  // back to showing everything until the request resolves (or if it fails).
  const { sections: enabledSections } = useAssessmentSections(
    reportDetails?.assessment_summary?.assessment_id,
  );

  // Scores
  const score = reportDetails?.response_details?.total_score ? parseFloat(reportDetails.response_details.total_score) : undefined;
  const basicExpectationScore = basicExpectation?.score;
  const workStyleScore = workStyle?.score;
  const workplaceQualitiesScore = workplaceQualities?.score;
  const workMotivatorsScore = workMotivator?.score;
  const personalityScore = personality?.score;
  const domainKnowledgeScore = domainKnowledge?.score;

  // Helper functions (copied from report)
  const transformBasicExpectationToTable = () => {
    if (!basicExpectation || !basicExpectation.candidate_response || !basicExpectation.employer_expectation) {
      return [];
    }

    const { candidate_response, employer_expectation } = basicExpectation;
    const shouldShowMetric = (flag?: boolean) => flag !== false;

    const formatExperienceLevel = (level: string) => {
      if (!level) return 'N/A';
      return level.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const formatYears = (years: string) => {
      if (!years) return 'N/A';
      return years.replace(/_/g, '-').replace(/\b\w/g, l => l.toUpperCase()) + ' years';
    };

    const { comparisons } = basicExpectation;

    const tableItems: { label: string; expectation: string; response: string; result: string; }[] = [];

    if (shouldShowMetric(employer_expectation.require_experience_level)) {
      tableItems.push({
        label: 'Experience level',
        expectation: formatExperienceLevel(employer_expectation.experience_level),
        response: formatExperienceLevel(candidate_response.experience_level),
        result: comparisons?.experience_level?.result || 'conflict',
      });
    }

    if (shouldShowMetric(employer_expectation.require_years_of_experience)) {
      tableItems.push({
        label: 'Years of experience',
        expectation: formatYears(employer_expectation.years_of_experience),
        response: formatYears(candidate_response.years_of_experience),
        result: comparisons?.years_of_experience?.result || 'conflict',
      });
    }

    if (shouldShowMetric(employer_expectation.require_employment_type)) {
      tableItems.push({
        label: 'Employment Type',
        expectation: formatExperienceLevel(employer_expectation.employment_type),
        response: formatExperienceLevel(candidate_response.employment_type),
        result: comparisons?.employment_type?.result || 'conflict',
      });
    }

    if (employer_expectation.work_mode && shouldShowMetric(employer_expectation.require_work_mode)) {
      tableItems.push({
        label: 'Work Mode',
        expectation: formatExperienceLevel(employer_expectation.work_mode),
        response: formatExperienceLevel(candidate_response.work_mode || ''),
        result: comparisons?.work_mode?.result || 'conflict',
      });
    }

    if (shouldShowMetric(employer_expectation.require_salary_range)) {
      tableItems.push({
        label: 'Salary Expectation',
        expectation: formatSalaryRange(employer_expectation),
        response: formatSalaryExpectation(candidate_response.salary_expectation, employer_expectation.salary_range_currency) || 'N/A',
        result: comparisons?.salary_expectation?.result || 'conflict',
      });
    }

    if (shouldShowMetric(employer_expectation.require_work_environment)) {
      tableItems.push({
        label: 'Experience environment',
        expectation: formatExperienceLevel(employer_expectation.experience_environment),
        response: formatExperienceLevel(candidate_response.experience_environment),
        result: comparisons?.experience_environment?.result || 'conflict',
      });
    }

    return tableItems;
  };

  const basicExpectationTableItems = transformBasicExpectationToTable();
  const showCvRelevance = basicExpectation?.employer_expectation?.require_cv_upload !== false && !!basicExpectation?.cv_relevance;

  const transformSoftSkillsToChart = () => {
    if (!softSkills?.soft_skills || !Array.isArray(softSkills.soft_skills)) return [];

    return softSkills.soft_skills.map((skill) => ({
      name: skill?.name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A',
      expectation: skill?.score?.expectation || 0,
      result: skill?.score?.candidate_result || 0,
      resultLabel: skill?.score?.score_range ? skill.score.score_range.charAt(0).toUpperCase() + skill.score.score_range.slice(1) : 'N/A',
      color: getScoreColor(skill.score?.candidate_result || 0),
    }));
  };

  const transformSoftSkillsToAnalysis = () => {
    if (!softSkills?.soft_skills || !Array.isArray(softSkills.soft_skills)) return [];

    return softSkills.soft_skills.map((skill) => ({
      title: skill?.name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A',
      score: skill?.score?.candidate_result || 0,
      level: skill?.score?.score_range ? skill.score.score_range.charAt(0).toUpperCase() + skill.score.score_range.slice(1) : 'N/A',
      sections: [
        {
          title: 'Implication',
          items: [skill?.score?.implication || 'N/A'],
        },
        {
          title: 'Impact on Work',
          items: [skill?.score?.impact_on_work || 'N/A'],
        },
      ].filter(section => section.items[0] !== 'N/A'),
    }));
  };

  const transformWorkStyleToChart = () => {
    if (!workStyle) return [];

    const dimensions = [
      { key: 'prioritizing', name: 'Prioritizing' },
      { key: 'visualizing', name: 'Visualizing' },
      { key: 'planning', name: 'Planning' },
      { key: 'coordinating', name: 'Coordinating' },
    ];

    return dimensions
      .map((dim) => {
        const data = workStyle[dim.key as keyof typeof workStyle] as any;
        if (!data || typeof data !== 'object') return null;

        return {
          name: dim.name,
          expectation: data.expectation || 0,
          result: data.candidate_result || 0,
          resultLabel: data.score_range?.charAt(0).toUpperCase() + data.score_range?.slice(1) || 'N/A',
          color: getScoreColor(data.candidate_result || 0),
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  };

  const transformWorkStyleToAnalysis = () => {
    if (!workStyle) return [];

    const dimensions = [
      { key: 'prioritizing', name: 'Prioritizing' },
      { key: 'visualizing', name: 'Visualizing' },
      { key: 'planning', name: 'Planning' },
      { key: 'coordinating', name: 'Coordinating' },
    ];

    return dimensions
      .map((dim) => {
        const data = workStyle[dim.key as keyof typeof workStyle] as any;
        if (!data || typeof data !== 'object') return null;

        return {
          title: dim.name,
          score: data.candidate_result || 0,
          level: data.score_range?.charAt(0).toUpperCase() + data.score_range?.slice(1) || 'N/A',
          sections: [
            {
              title: 'Implication',
              items: [data.implication || 'N/A'],
            },
            {
              title: 'Impact on Work',
              items: [data.impact_on_work || 'N/A'],
            },
            {
              title: 'Opportunities for Growth',
              items: Array.isArray(data.opportunities_for_growth) ? data.opportunities_for_growth : [],
            },
          ].filter(section => section.items.length > 0),
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  };

  const transformWorkplaceQualitiesToChart = () => {
    if (!workplaceQualities?.qualities || !Array.isArray(workplaceQualities.qualities)) return [];

    return workplaceQualities.qualities.map((quality) => ({
      name: quality?.title ? quality.title.charAt(0).toUpperCase() + quality.title.slice(1) : 'N/A',
      expectation: Array.isArray(workplaceQualities.employer_expectation) ? (workplaceQualities.employer_expectation.find(e => e?.title === quality?.title)?.score || 0) : 0,
      result: quality?.score || 0,
      resultLabel: quality?.score_range ? quality.score_range.charAt(0).toUpperCase() + quality.score_range.slice(1) : 'N/A',
      color: getScoreColor(quality?.score || 0),
    }));
  };

  const transformWorkplaceQualitiesToAnalysis = () => {
    if (!workplaceQualities?.qualities || !Array.isArray(workplaceQualities.qualities)) return [];

    return workplaceQualities.qualities.map((quality) => ({
      title: quality?.title ? quality.title.charAt(0).toUpperCase() + quality.title.slice(1) : 'N/A',
      score: quality?.score || 0,
      level: quality?.score_range ? quality.score_range.charAt(0).toUpperCase() + quality.score_range.slice(1) : 'N/A',
      sections: [
        {
          title: 'What this means for you',
          inline: true,
          items: [quality?.what_this_tells_you].filter(Boolean),
        },
        {
          title: 'In the role, expect them to',
          inline: true,
          items: [quality?.in_the_role].filter(Boolean),
        },
        {
          title: 'Watch out for',
          inline: true,
          items: [quality?.watch_out_for].filter(Boolean),
        },
        {
          title: 'Best suited for roles requiring',
          inline: true,
          items: [quality?.works_best_in].filter(Boolean),
        },
      ].filter(section => section.items.length > 0),
    }));
  };

  const transformWorkMotivatorsToAnalysis = () => {
    if (!workMotivator?.candidate_result || !Array.isArray(workMotivator.candidate_result)) return [];

    return workMotivator.candidate_result.map((result) => {
      const matchStatus = result?.match ? '✓ Matches Employer Expectation' : '✗ Does Not Match Employer Expectation';

      return {
        title: result?.details?.name || 'N/A',
        score: result?.details?.score || 0,
        level: `${result?.details?.score_range ? result.details.score_range.charAt(0).toUpperCase() + result.details.score_range.slice(1) : 'N/A'} - ${matchStatus}`,
        sections: [
          {
            title: 'What this tells you',
            inline: true,
            items: [result?.details?.what_this_tells_you].filter(Boolean),
          },
          {
            title: 'In the role, expect them to',
            inline: true,
            items: [result?.details?.in_the_role].filter(Boolean),
          },
          {
            title: 'Watch out for',
            inline: true,
            items: [result?.details?.watch_out_for].filter(Boolean),
          },
          {
            title: 'Works best in environments that',
            inline: true,
            items: [result?.details?.works_best_in].filter(Boolean),
          },
        ].filter(section => section.items.length > 0),
      };
    });
  };

  const transformOtherKeyMotivatorsToAnalysis = () => {
    const otherKeyMotivators =
      workMotivator?.other_key_motivators ||
      workMotivator?.otherKeyMotivators ||
      workMotivator?.key_motivators ||
      workMotivator?.motivators;

    if (!otherKeyMotivators || !Array.isArray(otherKeyMotivators)) return [];

    return otherKeyMotivators.map((motivator) => {
      const details = motivator?.details || motivator || {};

      return {
        title: details?.name || motivator?.title || motivator?.name || 'N/A',
        score: details?.score || motivator?.score || 0,
        level: `${details?.score_range ? details.score_range.charAt(0).toUpperCase() + details.score_range.slice(1) : 'N/A'}`,
        sections: [
          {
            title: 'What this tells you',
            inline: true,
            items: [details?.what_this_tells_you].filter(Boolean),
          },
          {
            title: 'In the role, expect them to',
            inline: true,
            items: [details?.in_the_role].filter(Boolean),
          },
          {
            title: 'Watch out for',
            inline: true,
            items: [details?.watch_out_for].filter(Boolean),
          },
          {
            title: 'Works best in environments that',
            inline: true,
            items: [details?.works_best_in].filter(Boolean),
          },
        ].filter(section => section.items.length > 0),
      };
    });
  };

  const transformEmployerExpectationsToAnalysis = () => {
    if (!workMotivator?.expectations || !Array.isArray(workMotivator.expectations)) return [];

    return workMotivator.expectations.map((exp) => {
      const matchStatus = exp?.match ? '✓ Matches Candidate Result' : '✗ Does Not Match Candidate Result';

      return {
        title: exp?.details?.name || 'N/A',
        score: exp?.details?.score || 0,
        level: `${exp?.details?.score_range ? exp.details.score_range.charAt(0).toUpperCase() + exp.details.score_range.slice(1) : 'N/A'} - ${matchStatus}`,
        sections: [
          {
            title: 'What this tells you',
            inline: true,
            items: [exp?.details?.what_this_tells_you].filter(Boolean),
          },
          {
            title: 'In the role, expect them to',
            inline: true,
            items: [exp?.details?.in_the_role].filter(Boolean),
          },
          {
            title: 'Watch out for',
            inline: true,
            items: [exp?.details?.watch_out_for].filter(Boolean),
          },
          {
            title: 'Works best in environments that',
            inline: true,
            items: [exp?.details?.works_best_in].filter(Boolean),
          },
        ].filter(section => section.items.length > 0),
      };
    });
  };

  const workMotivatorsAnalysis = transformWorkMotivatorsToAnalysis();
  const otherKeyMotivatorsAnalysis = transformOtherKeyMotivatorsToAnalysis();
  const employerExpectationsAnalysis = transformEmployerExpectationsToAnalysis();

  const transformPersonalityToAnalysis = () => {
    if (!personality?.core_areas || !Array.isArray(personality.core_areas)) return [];

    const skills: {
      title: string;
      score: number;
      sections: { title: string; items: string[] }[];
    }[] = [];

    personality.core_areas.forEach((area) => {
      if (!area) return;

      // Use the dominant boundary (top_boundary) for the analysis card
      const boundary = area.top_boundary || area.bottom_boundary;
      if (boundary) {
        const sections: { title: string; items: string[] }[] = [];

        if (boundary.description) {
          sections.push({
            title: 'Description',
            items: [boundary.description],
          });
        }

        if (Array.isArray(boundary.implications) && boundary.implications.length > 0) {
          sections.push({
            title: 'Implication',
            items: boundary.implications,
          });
        }

        skills.push({
          title: boundary.name || area.title || 'N/A',
          score: boundary.score || 0,
          sections,
        });
      }
    });

    return skills;
  };

  const ScoreBadge: React.FC<{ score: number; max?: number }> = ({ score, max = 10 }) => {
    const percentage = (score / max) * 100;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${getScoreBgClass(percentage)}`}>
        {score}/{max}
      </span>
    );
  };

  if (detailsLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#F5FAFF]">
        <p>Loading report...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5FAFF]">
      {/* Fixed Navbar */}
      <nav className="fixed top-0 left-0 right-0 h-[90px] bg-[#E8F3FF] border-b border-[#EAECF0] z-[1000] flex items-center px-6 md:px-6">
        <div className="max-w-[1440px] mx-auto w-full">
          <LogoWithText className="h-8 w-auto md:h-10" />
        </div>
      </nav>

      {/* Main Content */}
      <div className="mt-[90px] flex gap-[1.7%] relative md:flex-row flex-col md:mx-[34px] mx-0 pt-[20px]">
        {/* Sidebar */}
        <aside className="min-[1150px]:p-4 md:p-2 p-4 md:w-[23.7%] md:mx-0 mx-4 md:sticky md:top-[94px] grid md:grid-cols-1 sm:grid-cols-3 grid-cols-1 md:mb-0 mb-8 bg-white rounded-xl shadow-[0_1px_3px_rgba(16,24,40,0.1),0_1px_2px_rgba(16,24,40,0.06)] h-fit">
          {/* Score Circle */}
          <div className="relative w-[140px] h-[140px] md:w-[140px] md:h-[140px] mx-auto mb-5 md:mb-5 sm:mb-0 mt-5">
            {score !== undefined ? (
              <>
                <DoughnutChart
                  cutout='82%'
                  score={score}
                  className="w-full h-full"
                  borderRadius={[20, 0]}
                  total={100}
                />
                <p
                  className={`absolute "min-[490px]:top-[45%] top-[40%] min-[490px]:left-1/2 left-[44%] -translate-x-1/2 -translate-y-1/2 text-[28px] md:text-[28px] font-semibold leading-none ${getScoreTextClass(score)}`}
                >
                  {score}%
                </p>
                <p className="absolute min-[490px]:bottom-[35%] bottom-[40%] min-[490px]:left-1/2 left-[44%] -translate-x-1/2 text-[11px] md:text-[11px] text-[#667085]">avg.</p>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-center">
                <p className="text-[#667085] text-sm">Score not available</p>
              </div>
            )}
          </div>

          {/* Candidate Info */}
          <div className="text-center mx-auto mb-5 mt-0 w-full min-[1150px]:w-[84%]">
            <h2 className="text-[#1D2939] mb-[10px] font-medium sm:text-[19px] text-2xl">
              {formatPersonName(reportDetails?.response_details?.full_name || '') || 'Candidate'}
            </h2>
            <p className="text-[#667085] my-2 mx-0 sm:text-sm text-base break-words">{reportDetails?.response_details?.email || ''}</p>
            <p className="text-[#667085] mx-0 text-sm">
              {reportDetails?.response_details?.phone_number || ''} {reportDetails?.response_details?.phone_number && reportDetails?.response_details?.created_at ? '|' : ''} {reportDetails?.response_details?.created_at ? new Date(reportDetails.response_details.created_at).toLocaleDateString() : ''}
            </p>
            <div className="flex justify-center gap-4 mx-0 min-[1150px]:mt-5 min-[1150px]:mb-6 my-3">
              {reportDetails?.response_details?.portfolio && (
                <a href={reportDetails.response_details.portfolio} target="_blank" rel="noopener noreferrer" className="flex items-center gap-[6px] text-[#175CD3] xl:text-[0.9rem] text-sm hover:underline">
                  <CopyLinkIcon className="w-5 [&>path]:fill-[#175CD3] [&>path]:stroke-[0.2]" />
                  Portfolio
                </a>
              )}
              {reportDetails?.response_details?.resume && (
                <a href={reportDetails.response_details.resume} target="_blank" rel="noopener noreferrer" className="flex items-center gap-[6px] text-[#175CD3] xl:text-[0.9rem] text-sm hover:underline">
                  <CopyLinkIcon className="w-5 [&>path]:fill-[#175CD3] [&>path]:stroke-[0.2]" />
                  CV
                </a>
              )}
            </div>
            <div className="border-b-[1.5px] border-[#EAECF0] w-[200px] mx-auto" />
          </div>

          {/* Comments Button - Only visible on mobile */}
          {/* <div className="flex md:hidden mx-auto mt-2 items-center gap-[6px] text-[#175CD3] xl:text-[0.9rem] text-sm hover:underline cursor-pointer">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M17.5 12.5C17.5 12.942 17.3244 13.3659 17.0118 13.6785C16.6993 13.9911 16.2754 14.1667 15.8333 14.1667H5.83333L2.5 17.5V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H15.8333C16.2754 2.5 16.6993 2.67559 17.0118 2.98816C17.3244 3.30072 17.5 3.72464 17.5 4.16667V12.5Z" stroke="#175CD3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Comments (0)
          </div> */}
        </aside>

        {/* Report Content */}
        <main className="md:w-[74.6%] w-full bg-white rounded-xl flex flex-col gap-8 md:gap-8">
          {/* Basic Expectations */}
          {isSectionEnabled(enabledSections, 'basic_expectations') && (
          <section className="p-8 sm:p-3 md:p-8" id="basic-expectations">
            <div className="flex gap-4 md:gap-4 items-start mb-6 md:mb-6 pb-6 md:pb-6 border-b border-[#EAECF0]">
              <div className="relative w-20 h-20 md:w-20 md:h-20 flex-shrink-0">
                <DoughnutChart
                  score={basicExpectationScore ?? 0}
                  cutout="70%"
                  total={100}
                  className="w-full h-full"
                />
                <span className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-base md:text-base font-semibold ${getScoreTextClass(basicExpectationScore ?? 0)}`}>{basicExpectationScore ?? 0}%</span>
              </div>
              <div>
                <h2 className="text-xl md:text-xl font-semibold text-[#101828] mb-1">Basic Expectations</h2>
                <p className="text-[14px] md:text-[14px] text-[#667085] leading-relaxed">Conceptual thinking, issue identification, key information extraction for workable solutions.</p>
              </div>
            </div>

            {basicExpectationTableItems.length > 0 && (
              <BasicExpectationsTable
                tableBodyItems={basicExpectationTableItems}
              />
            )}

            {showCvRelevance && (
              <div className="sm:mx-4 mt-6 mb-6 lg:w-[40%]">
                <div className="bg-[#F5FAFF] rounded-lg px-6 py-4 border border-[#D1E9FF]">
                  <h3 className="text-[#012169] font-medium sm:text-lg text-base mb-1">CV Relevance</h3>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-[#344054]">{basicExpectation.cv_relevance.level}</span>
                      </div>
                      <div className='flex items-center gap-4'>
                        <div className="w-full bg-[#E4E7EC] rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full ${getScoreBgClass(basicExpectation.cv_relevance.percentage)}`}
                            style={{ width: `${basicExpectation.cv_relevance.percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs font-medium text-[#344054]">{basicExpectation.cv_relevance.percentage}%</span>
                      </div>
                    </div>
                  </div>
                  {/* <p className="text-[#475467] text-sm leading-relaxed">{basicExpectation.cv_relevance.summary}</p> */}
                </div>
              </div>
            )}

            {/* Strengths and Gaps Section */}
            <div className="sm:mx-4 mb-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Strengths */}
                {basicExpectation?.strengths && basicExpectation.strengths.length > 0 && (
                  <div className="bg-white rounded-lg p-6 border border-[#D1E9FF]">
                    <h3 className="font-medium text-base mb-4 px-4 py-2 bg-[#EFF8FF] rounded-md text-[#012169] w-fit">Strengths</h3>
                    <ul className="space-y-3">
                      {basicExpectation.strengths.map((strength, index) => (
                        <li key={index} className="flex gap-2 text-sm text-[#475467]">
                          <span className="text-[#475467] mt-0.5">•</span>
                          <span>{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Gaps */}
                {basicExpectation?.gaps && basicExpectation.gaps.length > 0 && (
                  <div className="bg-white rounded-lg p-6 border border-[#D1E9FF]">
                    <h3 className="font-medium text-base mb-4 px-4 py-2 bg-[#FFE6E6] rounded-md text-[#B30000] w-fit">Gaps</h3>
                    <ul className="space-y-3">
                      {basicExpectation.gaps.map((gap, index) => (
                        <li key={index} className="flex gap-2 text-sm text-[#475467]">
                          <span className="text-[#475467] mt-0.5">•</span>
                          <span>{gap}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Implications Section */}
            {basicExpectation?.implications && basicExpectation.implications.length > 0 && (
              <div className="sm:mx-4 mb-6">
                <div className="bg-[#F5FAFF] rounded-lg p-6 border border-[#D1E9FF]">
                  <h3 className="font-medium text-base mb-4 px-4 py-2 bg-[#E6F3FF] rounded-md text-[#012169] w-fit">Implications</h3>
                  <ul className="space-y-3">
                    {basicExpectation.implications.map((implication, index) => (
                      <li key={index} className="flex gap-2 text-sm text-[#475467]">
                        <span className="text-[#475467] mt-0.5">•</span>
                        <span>{implication}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

          </section>
          )}

          {/* Soft Skills */}
          {isSectionEnabled(enabledSections, 'soft_skills') && (
          <section className="p-8 sm:p-3 md:p-8" id="soft-skills">
            <div className="flex gap-4 md:gap-4 items-start mb-6 md:mb-6 pb-6 md:pb-6 border-b border-[#EAECF0]">
              <div className="relative w-20 h-20 md:w-20 md:h-20 flex-shrink-0">
                <DoughnutChart
                  score={softSkills?.score ?? 0}
                  cutout="70%"
                  total={100}
                  className="w-full h-full"
                />
                <span className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-base md:text-base font-semibold ${getScoreTextClass(softSkills?.score ?? 0)}`}>{softSkills?.score ?? 0}%</span>
              </div>
              <div>
                <h2 className="text-xl md:text-xl font-semibold text-[#101828] mb-1">Soft Skills</h2>
                <p className="text-[14px] md:text-[14px] text-[#667085] leading-relaxed">Conceptual thinking, issue identification, key information extraction for workable solutions.</p>
              </div>
            </div>

            <div className="mt-5">
              <SkillsComparisonChart
                expectationLabel="Expectation"
                resultLabel="Candidate's Result"
                skills={transformSoftSkillsToChart()}
                maxValue={100}
                showPercentages={true}
              />
              <CollapsibleSection title='Analysis' skills={transformSoftSkillsToAnalysis()} />
            </div>
          </section>
          )}

          {/* Work Style */}
          {isSectionEnabled(enabledSections, 'work_style') && (
          <section className="p-8 sm:p-3 md:p-8" id="work-style">
            <div className="flex gap-4 md:gap-4 items-start mb-6 md:mb-6 pb-6 md:pb-6 border-b border-[#EAECF0]">
              <div className="relative w-20 h-20 md:w-20 md:h-20 flex-shrink-0">
                <DoughnutChart
                  score={workStyleScore ?? 0}
                  cutout="70%"
                  total={100}
                  className="w-full h-full"
                />
                <span className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-base md:text-base font-semibold ${getScoreTextClass(workStyleScore ?? 0)}`}>{workStyleScore ?? 0}%</span>
              </div>
              <div>
                <h2 className="text-xl md:text-xl font-semibold text-[#101828] mb-1">Work Style</h2>
                <p className="text-[14px] md:text-[14px] text-[#667085] leading-relaxed">Conceptual thinking, issue identification, key information extraction for workable solutions.</p>
              </div>
            </div>

            <div className="mt-5">
              <SkillsComparisonChart
                expectationLabel="Expectation"
                resultLabel="Candidate's Result"
                skills={transformWorkStyleToChart()}
                maxValue={100}
                showPercentages={true}
              />
              <CollapsibleSection title='Analysis' skills={transformWorkStyleToAnalysis()} />
            </div>
          </section>
          )}

          {/* Workplace Qualities */}
          {isSectionEnabled(enabledSections, 'workplace_quality') && (
          <section className="p-8 sm:p-3 md:p-8" id="workplace-qualities">
            <div className="flex gap-4 md:gap-4 items-start mb-6 md:mb-6 pb-6 md:pb-6 border-b border-[#EAECF0]">
              <div className="relative w-20 h-20 md:w-20 md:h-20 flex-shrink-0">
                <DoughnutChart
                  score={workplaceQualitiesScore ?? 0}
                  cutout="70%"
                  total={100}
                  className="w-full h-full"
                />
                <span className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-base md:text-base font-semibold ${getScoreTextClass(workplaceQualitiesScore ?? 0)}`}>{workplaceQualitiesScore ?? 0}%</span>
              </div>
              <div>
                <h2 className="text-xl md:text-xl font-semibold text-[#101828] mb-1">Workplace Qualities</h2>
                <p className="text-[14px] md:text-[14px] text-[#667085] leading-relaxed">Conceptual thinking, issue identification, key information extraction for workable solutions.</p>
              </div>
            </div>

            <div className="mt-5">
              <SkillsComparisonChart
                expectationLabel="Expectation"
                resultLabel="Candidate's Result"
                skills={transformWorkplaceQualitiesToChart()}
                maxValue={100}
                showPercentages={true}
              />
              <CollapsibleSection title='Analysis' skills={transformWorkplaceQualitiesToAnalysis()} />
            </div>
          </section>
          )}

          {/* Work Motivators */}
          {isSectionEnabled(enabledSections, 'work_culture') && (
          <section className="p-8 sm:p-3 md:p-8" id="work-motivators">
            <div className="flex gap-4 md:gap-4 items-start mb-6 md:mb-6 pb-6 md:pb-6 border-b border-[#EAECF0]">
              <div className="relative w-20 h-20 md:w-20 md:h-20 flex-shrink-0">
                <DoughnutChart
                  score={workMotivatorsScore ?? 0}
                  cutout="70%"
                  total={100}
                  className="w-full h-full"
                />
                <span className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-base md:text-base font-semibold ${getScoreTextClass(workMotivatorsScore ?? 0)}`}>{workMotivatorsScore ?? 0}%</span>
              </div>
              <div>
                <h2 className="text-xl md:text-xl font-semibold text-[#101828] mb-1">Work Motivators</h2>
                <p className="text-[14px] md:text-[14px] text-[#667085] leading-relaxed">Work motivators represent the key drivers that influence how a candidate approaches their work, what excites them, and how they are motivated to achieve results.</p>
              </div>
            </div>

            <div className="mt-5">
              {workMotivatorsAnalysis.length > 0 && (
                <CollapsibleSection title='Analysis' skills={workMotivatorsAnalysis} />
              )}
              {otherKeyMotivatorsAnalysis.length > 0 && (
                <CollapsibleSection title='Other Key Motivators' skills={otherKeyMotivatorsAnalysis} />
              )}
              {/* {employerExpectationsAnalysis.length > 0 && (
                <CollapsibleSection title='Employer Expectations' skills={employerExpectationsAnalysis} />
              )} */}
            </div>
          </section>
          )}

          {/* Personality */}
          {isSectionEnabled(enabledSections, 'personality') && (
          <section className="p-8 sm:p-3 md:p-8" id="personality">
            <div className="flex gap-4 md:gap-4 items-start mb-6 md:mb-6 pb-6 md:pb-6 border-b border-[#EAECF0]">
              <div className="relative w-20 h-20 md:w-20 md:h-20 flex-shrink-0">
                <DoughnutChart
                  score={personalityScore ?? 0}
                  cutout="70%"
                  total={100}
                  className="w-full h-full"
                />
                <span className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-base md:text-base font-semibold ${getScoreTextClass(personalityScore ?? 0)}`}>{personalityScore ?? 0}%</span>
              </div>
              <div>
                <h2 className="text-xl md:text-xl font-semibold text-[#101828] mb-1">Personality</h2>
                <p className="text-[14px] md:text-[14px] text-[#667085] leading-relaxed">Personality reflects how a candidate naturally approaches work, collaborates with others, processes information, solves problems, and makes decisions.</p>
                <PersonalityAlignmentBadge score={personalityScore} className="mt-3" />
              </div>
            </div>

            <div className="mt-5">
              {personality ? (
                <>
                  <PersonalityAlignmentChart
                    coreAreas={personality.core_areas}
                    employerExpectations={personality.employer_expectations}
                  />
                  {/* Personality Type Section */}
                  <div className="py-10">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Employer Preference */}
                      {personality.employer_preference && (
                        <div>
                          <h4 className="text-xl pb-2 font-semibold text-[#6B7280]">Preference</h4>
                          <div className="border border-[#D1E9FF] rounded-lg overflow-hidden shadow-[0px_4px_8px_0px_rgba(0,0,0,0.1)] h-full">
                            <div className="p-4">
                              <h5 className="text-[#012169] font-semibold md:text-base mb-4 px-3 py-2 bg-[#EFF8FF] rounded-md w-fit">
                                {personality.employer_preference.personality_type}
                              </h5>
                              <div className="mb-3">
                                <h6 className="text-sm font-semibold text-[#1D2939] mb-2">Strengths</h6>
                                <ul className="list-disc pl-5 space-y-1">
                                  {personality.employer_preference.strengths.map((s, idx) => (
                                    <li key={idx} className="text-sm text-[#475467]">
                                      <span className="font-medium">{s.title}:</span> {s.content}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h6 className="text-sm font-semibold text-[#1D2939] mb-2">Areas of Growth</h6>
                                <ul className="list-disc pl-5 space-y-1">
                                  {personality.employer_preference.growth_areas.map((a, idx) => (
                                    <li key={idx} className="text-sm text-[#475467]">
                                      <span className="font-medium">{a.title}:</span> {a.content}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Candidate Personality Type */}
                      <div>
                        <h4 className="text-xl pb-2 font-semibold text-[#6B7280]">Candidate Personality Type</h4>
                        <div className="border border-[#D1E9FF] rounded-lg overflow-hidden shadow-[0px_4px_8px_0px_rgba(0,0,0,0.1)] h-full">
                          <div className="p-4">
                            <h5 className="text-[#012169] font-semibold md:text-base mb-4 px-3 py-2 bg-[#EFF8FF] rounded-md w-fit">
                              {personality.personality_type || 'N/A'}
                            </h5>
                            {personality.description && (
                              <p className="text-sm text-[#475467] mb-4">{personality.description}</p>
                            )}
                            <div className="mb-3">
                              <h6 className="text-sm font-semibold text-[#1D2939] mb-2">Strengths</h6>
                              <ul className="list-disc pl-5 space-y-1">
                                {Array.isArray(personality?.strengths) && personality.strengths.map((strength, idx) => (
                                  <li key={idx} className="text-sm text-[#475467]">
                                    <span className="font-medium">{strength.title}:</span> {strength.content}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h6 className="text-sm font-semibold text-[#1D2939] mb-2">Areas of Growth</h6>
                              <ul className="list-disc pl-5 space-y-1">
                                {Array.isArray(personality?.growth_areas) && personality.growth_areas.map((area, idx) => (
                                  <li key={idx} className="text-sm text-[#475467]">
                                    <span className="font-medium">{area.title}:</span> {area.content}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Collapsible Analysis Section */}
                  <CollapsibleSection title='Analysis' skills={transformPersonalityToAnalysis()} />
                </>
              ) : (
                <p>No personality data available</p>
              )}
            </div>
          </section>
          )}

          {/* Domain Knowledge */}
          {isSectionEnabled(enabledSections, 'domain_knowledge') && (
          <section className="p-8 sm:p-3 md:p-8" id="domain-knowledge">
            <div className="flex gap-4 md:gap-4 items-start mb-6 md:mb-6 pb-6 md:pb-6 border-b border-[#EAECF0]">
              <div className="relative w-20 h-20 md:w-20 md:h-20 flex-shrink-0">
                <DoughnutChart
                  score={domainKnowledgeScore ?? 0}
                  cutout="70%"
                  total={100}
                  className="w-full h-full"
                />
                <span className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-base md:text-base font-semibold ${getScoreTextClass(domainKnowledgeScore ?? 0)}`}>{domainKnowledgeScore ?? 0}%</span>
              </div>
              <div>
                <h2 className="text-xl md:text-xl font-semibold text-[#101828] mb-1">Domain Knowledge</h2>
                <p className="text-[14px] md:text-[14px] text-[#667085] leading-relaxed">Assessment of technical knowledge and domain-specific expertise.</p>
              </div>
            </div>

            {(domainKnowledge?.results && Array.isArray(domainKnowledge.results) && domainKnowledge.results.length > 0) && (
              <DomainKnowledgeResults domainKnowledge={domainKnowledge} ScoreBadge={ScoreBadge} />
            )}
          </section>
          )}
        </main>
      </div>
    </div >
  );
};

export { PublicReportUI };
